import type { VRMCore } from '@pixiv/three-vrm-core'
import type { Ref } from 'vue'

import { Euler, MathUtils, Quaternion } from 'three'
import { ref } from 'vue'

interface GestureConfig {
  enabled: boolean
  intensity: number
  blendSpeed: number // Lower = heavier/smoother, Higher = snappier
  debugContinuous?: boolean
}

/**
 * Organic noise generator
 */
function noise(t: number): number {
  return Math.sin(t) * 0.5 + Math.sin(t * 2.1) * 0.25 + Math.sin(t * 4.3) * 0.125
}

interface Pose {
  [boneName: string]: { x?: number, y?: number, z?: number }
}

/**
 * UPDATED POSES:
 * - Much higher 'x' values on LowerArm (Elbow Bending)
 * - UpperArms brought closer to ribs for conversational posture
 * - Wrists bent to break the straight line
 */
/**
 * UPDATED POSES (Fixing the "Leaning Back" Issue)
 * AXIS CORRECTIONS FOR THIS MODEL:
 * - Spine/Chest X: NEGATIVE = Lean Forward (Positive was leaning back)
 * - UpperArm X: NEGATIVE = Swing Forward (Positive was swinging back)
 * - UpperArm Z: Positive/Negative as established in previous fix (Left -, Right +)
 */
/**
 * FINAL AXIS CORRECTION:
 * - UpperArm X: POSITIVE = Swing Forward (Negative was swinging back)
 * - LowerArm X: NEGATIVE = Bend Elbow (Positive was straightening/hyperextending)
 * - UpperArm Z: Left(-) / Right(+) = Down (Confirmed correct from idle)
 */
/**
 * FINAL AXIS CORRECTION:
 * - UpperArm X: POSITIVE = Swing Forward (Negative was swinging back)
 * - LowerArm X: NEGATIVE = Bend Elbow (Positive was straightening/hyperextending)
 * - UpperArm Z: Left(-) / Right(+) = Down (Confirmed correct from idle)
 */
const POSES: Record<string, Pose> = {
  'idle': {
    // Relaxed A-pose
    leftUpperArm: { z: -75, y: 10, x: 5 }, // CHANGED: x: 5 (slightly forward)
    rightUpperArm: { z: 75, y: -10, x: 5 }, // CHANGED: x: 5
    leftLowerArm: { x: 20, y: -10 }, // CHANGED: -10 (soft bend)
    rightLowerArm: { x: 20, y: 10 }, // CHANGED: -10
    leftHand: { x: 15, y: -15 },
    rightHand: { x: 15, y: 15 },
    spine: { x: 2, y: 0 },
    head: { x: 0, y: 0 },
  },

  'hello1': {
    // Presenting with right hand (Elbow tucked in)
    // x: 20 swings forward.
    rightUpperArm: { z: 30, x: 70, y: 10 },
    // x: -85 bends the elbow deep
    rightLowerArm: { x: -140, y: 120, z: 10 },
    rightHand: { x: -30, y: -10 },
    spine: { y: -5, x: 2 },
    head: { y: 8, x: -2 },

    // Left arm relaxed
    leftUpperArm: { z: -78, x: 5 },
    leftLowerArm: { x: -10 },
  },

  'hello2': {
    // Presenting with right hand (Elbow tucked in)
    // x: 20 swings forward.
    rightUpperArm: { z: 30, x: 70, y: 10 },
    // x: -85 bends the elbow deep
    rightLowerArm: { x: -120, y: 80 },
    rightHand: { x: -30, y: -10 },
    spine: { y: -5, x: 2 },
    head: { y: 8, x: -2 },

    // Left arm relaxed
    leftUpperArm: { z: -78, x: 5 },
    leftLowerArm: { x: -10 },
  },

  'thinking': {
    // Hand to chin
    // x: 60 brings elbow FORWARD in front of chest
    rightUpperArm: { z: 20, x: 60, y: 10 },
    // x: -135 bends elbow completely to touch face
    rightLowerArm: { x: -135 },
    rightHand: { x: 20, y: 10, z: -10 },
    head: { z: -5, y: -10, x: 10 },
    spine: { x: 5, y: 2 },

    leftUpperArm: { z: -75 },
    leftLowerArm: { x: -10 },
  },

  'explain-both': {
    // Arms out to sides, elbows bent
    // x: 20 brings elbows forward (conversational)
    leftUpperArm: { z: -50, x: -20, y: 20 },
    leftLowerArm: { x: 70, z: 20, y: -80 }, // BEND
    leftHand: { x: -20, y: 20 },

    rightUpperArm: { z: 50, x: -20, y: -20 },
    rightLowerArm: { x: 70, z: -20, y: 80 }, // BEND
    rightHand: { x: -20, y: -20 },

    spine: { x: 3 },
    head: { x: -3 },
  },

  'explain-both2': {
    // Arms out to sides, elbows bent (copy of explain-both for customization)
    leftUpperArm: { z: -70, x: -20, y: 20 },
    leftLowerArm: { z: 60, x: 90, y: -80 }, // BEND
    leftHand: { x: -20, y: 20 },

    rightUpperArm: { z: 70, x: -20, y: -20 },
    rightLowerArm: { z: -60, x: 90, y: 80 }, // BEND
    rightHand: { x: -20, y: -20 },

    spine: { x: 3 },
    head: { x: -3 },
  },

  'shrug': {
    // Shoulders raised, palms up
    leftUpperArm: { z: -60, x: 10, y: -30 },
    leftLowerArm: { x: -90, y: -30 }, // BEND
    leftHand: { x: -40 },

    rightUpperArm: { z: 60, x: 10, y: 30 },
    rightLowerArm: { x: -90, y: 30 }, // BEND
    rightHand: { x: -40 },

    head: { z: 5 },
    spine: { x: -2 },
  },

  'shrug2': {
    // Shoulders raised, palms up (copy of shrug for customization)
    leftUpperArm: { z: -50, x: 10, y: -30 },
    leftLowerArm: { x: -90, y: -60 }, // BEND
    leftHand: { x: -40 },

    rightUpperArm: { z: 50, x: 10, y: 30 },
    rightLowerArm: { x: -90, y: 60 }, // BEND
    rightHand: { x: -40 },

    head: { z: 5 },
    spine: { x: -2 },
  },
}

export const GESTURE_TYPES = ['hello1', 'hello2', 'explain-both', 'explain-both2', 'thinking', 'shrug', 'shrug2'] as const
export type GestureType = typeof GESTURE_TYPES[number] | 'idle'

// Gestures used when speaking (cycles rapidly 0.2-0.5s)
export const SPEAKING_GESTURES = ['explain-both', 'explain-both2', 'shrug', 'shrug2'] as const

// Hello wave sequence: hello2 → hello1 → hello2 → hello1 → hello2 → hello1 → hello2 → idle
export const HELLO_WAVE_SEQUENCE = ['hello2', 'hello1', 'hello2', 'hello1', 'hello2', 'hello1', 'hello2', 'idle'] as const

// Common greeting patterns for detection
export const GREETING_PATTERNS = [
  /\bhello\b/i,
  /\bhi\b/i,
  /\bhey\b/i,
  /\bgreetings?\b/i,
  /\bwelcome\b/i,
  /\bgood\s*(morning|afternoon|evening|day)\b/i,
  /\bhowdy\b/i,
  /\byo\b/i,
  /\bwhat'?s?\s*up\b/i,
  /\bsup\b/i,
]

export const ALL_POSES = ['idle', ...GESTURE_TYPES] as const
export { POSES }

/**
 * Check if a message contains a greeting
 */
export function containsGreeting(message: string): boolean {
  return GREETING_PATTERNS.some(pattern => pattern.test(message))
}

// Gesture token patterns for LLM output
export const GESTURE_TOKENS = {
  GESTURE_HELLO: 'hello-wave',
  GESTURE_SHRUG: 'shrug',
  GESTURE_EXPLAIN: 'explain-both',
  GESTURE_THINK: 'thinking',
} as const

export type GestureToken = keyof typeof GESTURE_TOKENS

/**
 * Extract gesture token from text (e.g., <|GESTURE_HELLO|>)
 */
export function extractGestureToken(text: string): GestureToken | null {
  const match = text.match(/<\|?(GESTURE_\w+)\|?>/)
  if (match && match[1] in GESTURE_TOKENS) {
    return match[1] as GestureToken
  }
  return null
}

const CONTROLLED_BONES = [
  'spine',
  'chest',
  'neck',
  'head',
  'leftShoulder',
  'leftUpperArm',
  'leftLowerArm',
  'leftHand',
  'rightShoulder',
  'rightUpperArm',
  'rightLowerArm',
  'rightHand',
] as const

export function useVRMGestures(
  isSpeaking: Ref<boolean>,
  config: Ref<GestureConfig> = ref({ enabled: true, intensity: 1.0, blendSpeed: 4.0 }),
) {
  let time = 0
  let initialized = false

  const currentPose = ref<GestureType>('idle')
  const debugPoseOverride = ref<GestureType | null>(null) // Manual override for debug
  let gestureTimer = 0
  let gestureHoldDuration = 0.3

  // Hello wave state
  const isWaving = ref(false)
  const requestSmile = ref(false) // Signal to expression system to smile during wave
  let waveSequenceIndex = 0
  let waveStepTimer = 0
  const WAVE_STEP_DURATION = 0.25 // Each wave pose held for 0.25s

  // Initial wave on load (triggered once)
  let hasTriggeredInitialWave = false
  let initialWaveTimer = 0
  const INITIAL_WAVE_DELAY = 3.0 // Wait 3s after load before waving

  // Idle hello wave timer (periodic greeting at idle)
  let idleTimer = 0
  let nextIdleWaveTime = MathUtils.randFloat(25, 45) // Random wave every 25-45s of idle

  // Random idle smile (separate from wave)
  let idleSmileTimer = 0
  let nextIdleSmileTime = MathUtils.randFloat(8, 15) // Random smile every 8-15s

  const baseQuaternions: Record<string, Quaternion> = {}
  const currentQuaternions: Record<string, Quaternion> = {}

  function init(vrm: VRMCore) {
    if (!vrm?.humanoid)
      return
    CONTROLLED_BONES.forEach((name) => {
      const node = vrm.humanoid?.getNormalizedBoneNode(name as any)
      if (node) {
        baseQuaternions[name] = node.quaternion.clone()
        currentQuaternions[name] = node.quaternion.clone()
      }
    })
    initialized = true
  }

  /**
   * Trigger the hello wave sequence (hello1 → hello2 → hello1 → hello2 → idle)
   */
  function triggerWave() {
    if (isWaving.value)
      return // Already waving
    isWaving.value = true
    requestSmile.value = true
    waveSequenceIndex = 0
    waveStepTimer = 0
    currentPose.value = HELLO_WAVE_SEQUENCE[0] as GestureType
  }

  /**
   * Call this when a message is received to check for greetings
   */
  function onMessageReceived(message: string) {
    if (containsGreeting(message)) {
      triggerWave()
    }
  }

  /**
   * Handle a gesture token from LLM output (e.g., GESTURE_HELLO)
   */
  function handleGestureToken(token: GestureToken) {
    if (token === 'GESTURE_HELLO') {
      triggerWave()
      return
    }
    // For other gestures, just set the pose directly
    const pose = GESTURE_TOKENS[token]
    debugPoseOverride.value = pose as GestureType
    // Auto-clear after gesture duration
    setTimeout(() => {
      debugPoseOverride.value = null
    }, 2000)
  }

  /**
   * Process text for gesture tokens and trigger appropriate gestures
   */
  function processTextForGestures(text: string) {
    const token = extractGestureToken(text)
    if (token) {
      handleGestureToken(token)
    }
    // Also check for natural greetings
    if (containsGreeting(text)) {
      triggerWave()
    }
  }

  function update(vrm: VRMCore | undefined, delta: number) {
    if (!vrm?.humanoid || !config.value.enabled)
      return
    if (!initialized) {
      init(vrm); if (!initialized)
        return
    }

    time += delta

    // === Debug Pose Override ===
    // If a manual debug pose is set, skip the state machine entirely
    if (debugPoseOverride.value !== null) {
      currentPose.value = debugPoseOverride.value
    }
    // === Hello Wave Sequence ===
    else if (isWaving.value) {
      waveStepTimer += delta
      if (waveStepTimer >= WAVE_STEP_DURATION) {
        waveStepTimer = 0
        waveSequenceIndex++
        if (waveSequenceIndex >= HELLO_WAVE_SEQUENCE.length) {
          // Wave sequence complete
          isWaving.value = false
          requestSmile.value = false
          currentPose.value = 'idle'
          // Reset idle timer so we don't wave again immediately
          idleTimer = 0
          nextIdleWaveTime = MathUtils.randFloat(20, 40)
        }
        else {
          currentPose.value = HELLO_WAVE_SEQUENCE[waveSequenceIndex] as GestureType
        }
      }
    }
    else {
      const shouldGesticulate = isSpeaking.value || config.value.debugContinuous

      // === Speaking State Machine ===
      if (shouldGesticulate) {
        // Reset idle timer when speaking
        idleTimer = 0

        gestureTimer += delta
        if (gestureTimer >= gestureHoldDuration) {
          // Cycle through speaking gestures rapidly (0.2-0.5s)
          currentPose.value = SPEAKING_GESTURES[Math.floor(Math.random() * SPEAKING_GESTURES.length)] as GestureType
          gestureTimer = 0
          gestureHoldDuration = MathUtils.randFloat(0.2, 0.5)
        }
      }
      // === Idle State ===
      else {
        if (currentPose.value !== 'idle') {
          currentPose.value = 'idle'
          gestureTimer = 0
        }

        // Initial wave on character load (once, after 3s)
        if (!hasTriggeredInitialWave) {
          initialWaveTimer += delta
          if (initialWaveTimer >= INITIAL_WAVE_DELAY) {
            hasTriggeredInitialWave = true
            triggerWave()
          }
        }
        else {
          // Periodic hello wave at idle
          idleTimer += delta
          if (idleTimer >= nextIdleWaveTime) {
            triggerWave()
          }

          // Random idle smile (separate from wave)
          idleSmileTimer += delta
          if (idleSmileTimer >= nextIdleSmileTime && !isWaving.value) {
            requestSmile.value = true
            idleSmileTimer = 0
            nextIdleSmileTime = MathUtils.randFloat(10, 20)
            // Auto-clear smile after a moment
            setTimeout(() => {
              if (!isWaving.value) {
                requestSmile.value = false
              }
            }, 2000)
          }
        }
      }
    }

    // === Organic Noise Layers ===
    const intensity = config.value.intensity
    const breath = Math.sin(time * 1.5) * 0.02 * intensity // Chest breath
    const swayBody = noise(time * 0.3) * 0.03 * intensity // Core sway
    const swayHead = noise(time * 0.5 + 50) * 0.05 * intensity

    // ARM NOISE: Separate shoulder and elbow noise for "Kinematic Chain" effect
    const shoulderNoiseL = noise(time * 0.4) * 0.03 * intensity
    const shoulderNoiseR = noise(time * 0.4 + 100) * 0.03 * intensity

    // **KEY FIX**: Elbow breathing (Flexion/Extension noise)
    // We oscillate the elbow bend slightly so the arm isn't frozen stiff
    const elbowNoiseL = Math.sin(time * 0.7) * 0.05 * intensity
    const elbowNoiseR = Math.sin(time * 0.7 + 2) * 0.05 * intensity

    // === Apply ===
    const targetPose = POSES[currentPose.value] || POSES.idle
    const idlePose = POSES.idle

    CONTROLLED_BONES.forEach((boneName) => {
      const node = vrm.humanoid?.getNormalizedBoneNode(boneName as any)
      if (!node || !baseQuaternions[boneName])
        return

      const poseData = targetPose[boneName] || idlePose[boneName] || {}

      let nX = 0; let nY = 0; let nZ = 0

      // Apply specific noise channels to appropriate axes
      if (boneName === 'spine') {
        nX = breath; nY = swayBody
      }
      else if (boneName === 'head') {
        nY = swayHead; nX = swayHead * 0.5
      }
      else if (boneName.includes('UpperArm')) {
        // Upper arm sways on Z (up/down) and Y (twist)
        nZ = boneName.includes('left') ? shoulderNoiseL : shoulderNoiseR
        nY = nZ * 0.5
      }
      else if (boneName.includes('LowerArm')) {
        // ELBOW NOISE:
        // Since we bend with Negative X, we want the noise to generally pull it slightly more negative (bend)
        // or oscillate around the target.
        const noiseVal = boneName.includes('left') ? elbowNoiseL : elbowNoiseR
        // If our target is negative (bent), adding positive noise straightens it slightly.
        // This is fine and looks natural.
        nX = noiseVal
      }

      // Compute Target
      const targetEuler = new Euler(
        MathUtils.degToRad(poseData.x || 0) + nX,
        MathUtils.degToRad(poseData.y || 0) + nY,
        MathUtils.degToRad(poseData.z || 0) + nZ,
        'XYZ',
      )

      const targetOffset = new Quaternion().setFromEuler(targetEuler)
      const finalTarget = baseQuaternions[boneName].clone().multiply(targetOffset)

      // **KEY FIX**: Slerp Speed
      // We use a slightly slower blend speed to give the arms weight.
      // 4.0 is smoother than 8.0
      const slerpFactor = Math.min(1, delta * config.value.blendSpeed)
      currentQuaternions[boneName].slerp(finalTarget, slerpFactor)

      node.quaternion.copy(currentQuaternions[boneName])
    })
  }

  function reset(vrm: VRMCore) {
    if (!vrm)
      return
    debugPoseOverride.value = null
    currentPose.value = 'idle'
    update(vrm, 10)
  }

  /**
   * Set a specific pose for debugging. Pass null to return to normal behavior.
   */
  function setDebugPose(pose: GestureType | null) {
    debugPoseOverride.value = pose
  }

  return {
    init,
    update,
    reset,
    currentPose,
    setDebugPose,
    debugPoseOverride,
    // Hello wave
    triggerWave,
    onMessageReceived,
    isWaving,
    requestSmile,
    // Gesture token handling
    handleGestureToken,
    processTextForGestures,
  }
}
